import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import {
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import api from '../../services/api';

function formatCurrency(value) {
  const n = Number(value) || 0;
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatCurrencyWhole(value) {
  const n = Number(value) || 0;
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function formatGallons(value) {
  const n = Number(value) || 0;
  return `${n.toLocaleString(undefined, { maximumFractionDigits: 1 })} gal`;
}

function formatCount(value) {
  return String(Number(value) || 0);
}

function TrendChange({ pct, label = 'vs prior month' }) {
  if (pct == null) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
        <TrendingFlatIcon sx={{ fontSize: 16 }} /> {label}
      </Typography>
    );
  }
  const up = pct > 0;
  const down = pct < 0;
  const color = up ? 'success.main' : down ? 'error.main' : 'text.secondary';
  const Icon = up ? TrendingUpIcon : down ? TrendingDownIcon : TrendingFlatIcon;
  return (
    <Typography variant="caption" sx={{ color, display: 'flex', alignItems: 'center', gap: 0.25 }}>
      <Icon sx={{ fontSize: 16 }} />
      {pct > 0 ? '+' : ''}
      {pct}% {label}
    </Typography>
  );
}

function TrendBarChart({ periods, dataKey, formatValue, color = 'primary.main', getOpacity }) {
  if (!periods?.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        No data for this period.
      </Typography>
    );
  }
  const max = Math.max(...periods.map((p) => Number(p[dataKey]) || 0), 1);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: { xs: 0.5, sm: 1 },
        height: 200,
        pt: 1,
        overflowX: 'auto',
      }}
    >
      {periods.map((p, index) => {
        const value = Number(p[dataKey]) || 0;
        const heightPct = Math.max(4, (value / max) * 100);
        const opacity = getOpacity ? getOpacity(p, index) : value > 0 ? 1 : 0.25;
        const key = p.date || p.start_date || `${p.year}-${p.month}-${index}-${dataKey}`;
        return (
          <Box
            key={key}
            sx={{
              flex: '1 1 0',
              minWidth: 44,
              textAlign: 'center',
            }}
          >
            <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontSize: '0.65rem', lineHeight: 1.2 }}>
              {formatValue(value)}
            </Typography>
            <Box
              sx={{
                mx: 'auto',
                width: '100%',
                maxWidth: 56,
                height: `${heightPct}%`,
                minHeight: value > 0 ? 8 : 4,
                maxHeight: 140,
                bgcolor: color,
                borderRadius: '4px 4px 0 0',
                opacity,
              }}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.75, display: 'block', fontSize: '0.65rem', lineHeight: 1.1 }}
            >
              {p.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

function MonthProgressSection({ currentMonth }) {
  if (!currentMonth) return null;

  const { mtd, prior_mtd, prior_month_full, mtd_change_pct, progress_pct, day_of_month, days_in_month, label } =
    currentMonth;

  const progressCards = [
    { key: 'revenue', label: 'Revenue collected (MTD)', format: formatCurrencyWhole, changeKey: 'revenue' },
    { key: 'invoiced', label: 'Invoiced (MTD)', format: formatCurrencyWhole, changeKey: 'invoiced' },
    { key: 'jobs_completed', label: 'Jobs completed (MTD)', format: formatCount, changeKey: 'jobs_completed' },
    { key: 'diesel_gallons', label: 'Diesel delivered (MTD)', format: formatGallons, changeKey: 'diesel_gallons' },
  ];

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        {label} progress
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Day {day_of_month} of {days_in_month} ({progress_pct}% through the month)
      </Typography>
      <LinearProgress
        variant="determinate"
        value={progress_pct}
        sx={{ height: 8, borderRadius: 1, mb: 2 }}
      />
      <Grid container spacing={2}>
        {progressCards.map(({ key, label: cardLabel, format, changeKey }) => (
          <Grid item xs={12} sm={6} md={3} key={key}>
            <Card variant="outlined">
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="body2" color="text.secondary">
                  {cardLabel}
                </Typography>
                <Typography variant="h5">{format(mtd?.[key])}</Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  Same days last month: {format(prior_mtd?.[key])}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  Full prior month: {format(prior_month_full?.[key])}
                </Typography>
                <TrendChange pct={mtd_change_pct?.[changeKey]} label="vs same days last month" />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

function ThisMonthSection({ currentMonth }) {
  if (!currentMonth) {
    return (
      <Typography variant="body2" color="text.secondary">
        No current-month data available.
      </Typography>
    );
  }

  const dailyPeriods = (currentMonth.daily || []).filter((d) => !d.is_future);
  const weeklyPeriods = currentMonth.weekly || [];

  return (
    <>
      <MonthProgressSection currentMonth={currentMonth} />

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Daily — jobs completed
          </Typography>
          <TrendBarChart
            periods={dailyPeriods}
            dataKey="jobs_completed"
            formatValue={formatCount}
            color="#455A64"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Daily — revenue collected
          </Typography>
          <TrendBarChart
            periods={dailyPeriods}
            dataKey="revenue"
            formatValue={formatCurrencyWhole}
            color="#2E7D32"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Daily — diesel delivered
          </Typography>
          <TrendBarChart
            periods={dailyPeriods}
            dataKey="diesel_gallons"
            formatValue={formatGallons}
            color="#F4511E"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Weekly — jobs completed
          </Typography>
          <TrendBarChart
            periods={weeklyPeriods}
            dataKey="jobs_completed"
            formatValue={formatCount}
            color="#546E7A"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Weekly — revenue collected
          </Typography>
          <TrendBarChart
            periods={weeklyPeriods}
            dataKey="revenue"
            formatValue={formatCurrencyWhole}
            color="#388E3C"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Weekly — invoiced
          </Typography>
          <TrendBarChart
            periods={weeklyPeriods}
            dataKey="invoiced"
            formatValue={formatCurrencyWhole}
            color="#1565C0"
          />
        </Grid>
      </Grid>
    </>
  );
}

export default function ManagerReportsSection({
  customerId = null,
  customerName = '',
  driverId = null,
  driverName = '',
  vehicleId = null,
  vehicleName = '',
  embedded = false,
  reportsApiPath = '/managers/reports/',
  selfView = false,
}) {
  const [months, setMonths] = useState(6);
  const [viewTab, setViewTab] = useState(0);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const params = { months };
        if (!selfView && customerId) params.customer_id = customerId;
        if (!selfView && driverId) params.driver_id = driverId;
        if (!selfView && vehicleId) params.vehicle_id = vehicleId;
        const res = await api.get(reportsApiPath, { params });
        if (!cancelled) setReport(res.data);
      } catch (err) {
        if (!cancelled) {
          const status = err.response?.status;
          const detail =
            err.response?.data?.error ||
            err.response?.data?.detail ||
            (status === 404
              ? 'Reports API not found. Restart the Django backend to load the latest reports endpoint.'
              : 'Failed to load reports.');
          setError(detail);
          setReport(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [months, customerId, driverId, vehicleId, reportsApiPath, selfView]);

  const summary = report?.summary || {};
  const periods = report?.periods || [];
  const currentMonth = report?.current_month || null;

  const subtitle = selfView
    ? 'Monthly trends plus daily and weekly breakdown for the current month.'
    : vehicleId
      ? `Monthly trends and current-month progress for ${vehicleName || 'this vehicle'}.`
      : driverId
        ? `Monthly trends and current-month progress for ${driverName || 'this driver'}.`
        : customerId
          ? `Monthly trends and current-month progress for ${customerName || 'this customer'}.`
          : 'Monthly revenue, deliveries, and fuel — plus daily/weekly views for this month.';

  return (
    <Paper sx={{ p: embedded ? 2 : 3, mb: embedded ? 0 : 3 }} variant={embedded ? 'outlined' : 'elevation'}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          gap: 2,
          mb: 2,
        }}
      >
        <Box>
          <Typography
            variant={embedded ? 'h6' : 'h5'}
            component={embedded ? 'h2' : 'h1'}
            gutterBottom
            sx={{ mb: 0.5, fontWeight: 700 }}
          >
            Reports &amp; Trends
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 160, display: viewTab === 0 ? 'block' : 'none' }}>
          <InputLabel id="report-months-label">Period</InputLabel>
          <Select
            labelId="report-months-label"
            label="Period"
            value={months}
            onChange={(e) => setMonths(Number(e.target.value))}
          >
            <MenuItem value={3}>Last 3 months</MenuItem>
            <MenuItem value={6}>Last 6 months</MenuItem>
            <MenuItem value={12}>Last 12 months</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Tabs
        value={viewTab}
        onChange={(_, value) => setViewTab(value)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Monthly trends" />
        <Tab label={`This month${currentMonth?.label ? ` (${currentMonth.label})` : ''}`} />
      </Tabs>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={32} />
        </Box>
      ) : viewTab === 1 ? (
        <ThisMonthSection currentMonth={currentMonth} />
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Card variant="outlined">
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="body2" color="text.secondary">
                    Revenue collected
                  </Typography>
                  <Typography variant="h5">{formatCurrencyWhole(summary.revenue_total)}</Typography>
                  <TrendChange pct={summary.revenue_change_pct} />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card variant="outlined">
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="body2" color="text.secondary">
                    Invoiced
                  </Typography>
                  <Typography variant="h5">{formatCurrencyWhole(summary.invoiced_total)}</Typography>
                  <TrendChange pct={summary.invoiced_change_pct} />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card variant="outlined">
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="body2" color="text.secondary">
                    Jobs completed
                  </Typography>
                  <Typography variant="h5">{formatCount(summary.jobs_completed_total)}</Typography>
                  <TrendChange pct={summary.jobs_completed_change_pct} />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Card variant="outlined">
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="body2" color="text.secondary">
                    Diesel delivered
                  </Typography>
                  <Typography variant="h5">{formatGallons(summary.diesel_gallons_total)}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Fuel value: {formatCurrency(summary.diesel_fuel_value_total)}
                  </Typography>
                  <TrendChange pct={summary.diesel_gallons_change_pct} />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Card variant="outlined">
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="body2" color="text.secondary">
                    DEF delivered
                  </Typography>
                  <Typography variant="h5">{formatGallons(summary.def_gallons_total)}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Fuel value: {formatCurrency(summary.def_fuel_value_total)}
                  </Typography>
                  <TrendChange pct={summary.def_fuel_value_change_pct} />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Card variant="outlined">
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="body2" color="text.secondary">
                    Dyed delivered
                  </Typography>
                  <Typography variant="h5">{formatGallons(summary.dyed_gallons_total)}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Fuel value: {formatCurrency(summary.dyed_fuel_value_total)}
                  </Typography>
                  <TrendChange pct={summary.dyed_gallons_change_pct} />
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Revenue collected (paid invoices)
              </Typography>
              <TrendBarChart
                periods={periods}
                dataKey="revenue"
                formatValue={formatCurrencyWhole}
                color="#2E7D32"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Invoiced amount
              </Typography>
              <TrendBarChart
                periods={periods}
                dataKey="invoiced"
                formatValue={formatCurrencyWhole}
                color="#1565C0"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Diesel delivered
              </Typography>
              <TrendBarChart
                periods={periods}
                dataKey="diesel_gallons"
                formatValue={formatGallons}
                color="#F4511E"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                DEF fuel value
              </Typography>
              <TrendBarChart
                periods={periods}
                dataKey="def_fuel_value"
                formatValue={formatCurrency}
                color="#7B1FA2"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Diesel fuel value
              </Typography>
              <TrendBarChart
                periods={periods}
                dataKey="diesel_fuel_value"
                formatValue={formatCurrency}
                color="#E64A19"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Dyed delivered
              </Typography>
              <TrendBarChart
                periods={periods}
                dataKey="dyed_gallons"
                formatValue={formatGallons}
                color="#6D4C41"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Dyed fuel value
              </Typography>
              <TrendBarChart
                periods={periods}
                dataKey="dyed_fuel_value"
                formatValue={formatCurrency}
                color="#5D4037"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Jobs completed
              </Typography>
              <TrendBarChart
                periods={periods}
                dataKey="jobs_completed"
                formatValue={formatCount}
                color="#455A64"
              />
            </Grid>
          </Grid>
        </>
      )}
    </Paper>
  );
}
